/**
 * Per-case view state for the Fusion case page, kept in sessionStorage.
 *
 * What it remembers (and nothing else): camera (zoom, pan), the selected node
 * or edge id, the graph level (low or high), the aside tab, the Journal
 * replay step and the group boxes the officer expanded (they open folded). Positions are NOT stored (R24: the layout owns coordinates; the
 * layout is deterministic, see fusionLayout.sortForLayout); the camera is only
 * meaningful over the same laid-out picture, so it carries the level and a
 * graph signature and is ignored when either differs.
 *
 * One slot only: writing state for case B removes the entry of any other case
 * ("cleared when the case changes"), so the key space never grows.
 * Key: `fusion.viewState.<caseId>`. Every read and write is try/catch guarded.
 */
import type cytoscape from 'cytoscape';

export const VIEW_STATE_PREFIX = 'fusion.viewState.';
const VERSION = 1;
const MAX_ID_LEN = 200;
const MAX_BYTES = 4096;
const MAX_EXPANDED = 24;
const ZOOM_MIN = 0.05;
const ZOOM_MAX = 5;
const PAN_LIMIT = 1e6;

export type ViewLevel = 'low' | 'high';
export type ViewAside = 'inspector' | 'ledger' | 'import' | 'files';
const ASIDES: ViewAside[] = ['inspector', 'ledger', 'import', 'files'];

export interface ViewCamera { zoom: number; x: number; y: number }
export interface ViewSelection { id: string; kind: 'node' | 'edge' }

export interface CaseViewState {
  v: number;
  camera?: ViewCamera;
  /** Level and graph signature the camera was taken at. */
  cameraLevel?: ViewLevel;
  cameraSig?: string;
  /** `null` is stored as an absent field, a cleared selection removes the field. */
  selected?: ViewSelection;
  level?: ViewLevel;
  aside?: ViewAside;
  replayStep?: number;
  /** Ids of the group boxes the officer opened (everything else opens folded). */
  expanded?: string[];
}

const keyFor = (caseId: string) => `${VIEW_STATE_PREFIX}${caseId}`;
const finite = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

/** Drops anything malformed; the result is safe to apply. */
export function sanitizeViewState(raw: unknown): CaseViewState | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (r.v !== VERSION) return null;
  const out: CaseViewState = { v: VERSION };
  const c = r.camera as Record<string, unknown> | undefined;
  if (c && finite(c.zoom) && finite(c.x) && finite(c.y)
      && c.zoom >= ZOOM_MIN && c.zoom <= ZOOM_MAX && Math.abs(c.x) <= PAN_LIMIT && Math.abs(c.y) <= PAN_LIMIT) {
    out.camera = { zoom: c.zoom, x: c.x, y: c.y };
    if (r.cameraLevel === 'low' || r.cameraLevel === 'high') out.cameraLevel = r.cameraLevel;
    if (typeof r.cameraSig === 'string' && r.cameraSig.length <= 64) out.cameraSig = r.cameraSig;
  }
  const s = r.selected as Record<string, unknown> | undefined;
  if (s && typeof s.id === 'string' && s.id && s.id.length <= MAX_ID_LEN && (s.kind === 'node' || s.kind === 'edge')) {
    out.selected = { id: s.id, kind: s.kind };
  }
  if (r.level === 'low' || r.level === 'high') out.level = r.level;
  if (typeof r.aside === 'string' && (ASIDES as string[]).includes(r.aside)) out.aside = r.aside as ViewAside;
  if (finite(r.replayStep) && r.replayStep >= 1 && r.replayStep <= 100000) out.replayStep = Math.floor(r.replayStep);
  if (Array.isArray(r.expanded)) {
    const ids = r.expanded.filter((x): x is string => typeof x === 'string' && x.length > 0 && x.length <= MAX_ID_LEN).slice(0, MAX_EXPANDED);
    if (ids.length) out.expanded = ids;
  }
  return out;
}

export function readViewState(caseId: string | undefined): CaseViewState | null {
  if (!caseId) return null;
  try {
    const raw = sessionStorage.getItem(keyFor(caseId));
    return raw ? sanitizeViewState(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

/** Remove the remembered state of every case except `keep` (all when omitted). */
export function clearViewStates(keep?: string): void {
  try {
    const drop: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(VIEW_STATE_PREFIX) && k !== (keep ? keyFor(keep) : '')) drop.push(k);
    }
    drop.forEach((k) => sessionStorage.removeItem(k));
  } catch { /* storage unavailable */ }
}

/** Fields set to `null` are removed; others replace the stored value. */
export type ViewPatch = {
  [K in keyof Omit<CaseViewState, 'v'>]?: CaseViewState[K] | null;
};

export function writeViewState(caseId: string | undefined, patch: ViewPatch): void {
  if (!caseId) return;
  try {
    clearViewStates(caseId);
    const merged: Record<string, unknown> = { ...(readViewState(caseId) ?? {}), v: VERSION };
    Object.entries(patch).forEach(([k, val]) => {
      if (val === null) delete merged[k]; else if (val !== undefined) merged[k] = val;
    });
    const clean = sanitizeViewState(merged);
    if (!clean) return;
    const json = JSON.stringify(clean);
    if (json.length > MAX_BYTES) return;
    sessionStorage.setItem(keyFor(caseId), json);
  } catch { /* storage full or unavailable: the view just is not remembered */ }
}

/** A cheap fingerprint of the laid-out picture: the camera only fits the same graph. */
export function graphSignature(cy: cytoscape.Core): string {
  return `${cy.nodes().length}:${cy.edges().length}`;
}

export function captureCamera(cy: cytoscape.Core, level: ViewLevel): ViewPatch {
  const pan = cy.pan();
  return { camera: { zoom: cy.zoom(), x: pan.x, y: pan.y }, cameraLevel: level, cameraSig: graphSignature(cy) };
}

export interface ApplyResult { camera: boolean; selected: ViewSelection | null }

/**
 * Re-apply a remembered view onto a freshly laid-out instance. The camera is
 * applied only when level and graph signature match; a remembered selection is
 * returned only when the element still exists (the caller selects it).
 */
export function applyViewState(cy: cytoscape.Core, vs: CaseViewState | null, level: ViewLevel): ApplyResult {
  const result: ApplyResult = { camera: false, selected: null };
  if (!vs || cy.destroyed()) return result;
  if (vs.selected) {
    const el = cy.getElementById(vs.selected.id);
    if (el.nonempty() && (vs.selected.kind === 'node') === el.isNode()) result.selected = vs.selected;
  }
  const selectionGone = !!vs.selected && !result.selected;
  if (vs.camera && !selectionGone && (vs.cameraLevel ?? level) === level && vs.cameraSig === graphSignature(cy)) {
    cy.viewport({ zoom: vs.camera.zoom, pan: { x: vs.camera.x, y: vs.camera.y } });
    result.camera = true;
  }
  return result;
}
